"use client";

import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Mark, mergeAttributes, Node } from "@tiptap/core";
import { cn } from "@/utils";

// Custom Highlight mark that uses a configurable CSS class
const createHighlightMark = (highlightClassName: string) =>
  Mark.create({
    name: "highlight",

    parseHTML() {
      return [
        {
          tag: "span.tiptap-highlight",
        },
        {
          tag: "mark",
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return [
        "span",
        mergeAttributes(HTMLAttributes, { class: highlightClassName }),
        0,
      ];
    },
  });

interface TiptapHighlightContentProps {
  content: string | any;
  className?: string;
  highlightClassName?: string;
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
}

export default function TiptapHighlightContent({
  content,
  className = "",
  highlightClassName = "text-primary-300",
  as,
  ...props
}: TiptapHighlightContentProps) {
  // Send postMessage to Tina admin on click
  const handleTinaClick = () => {
    if (typeof window !== "undefined") {
      window.parent.postMessage({ type: "tiptap-highlight-click" }, "*");
    }
  };

  // Parse content before hooks (avoid conditional hook calls)
  let jsonContent = null;
  let isPlainText = false;

  if (content) {
    if (typeof content === "string") {
      const trimmed = content.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          jsonContent = JSON.parse(content);
        } catch {
          isPlainText = true;
        }
      } else {
        isPlainText = true;
      }
    } else if (typeof content === "object") {
      jsonContent = content;
    }
  }

  // Build extensions with custom paragraph node if 'as' prop is provided
  const extensions = [
    StarterKit.configure({
      heading: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      horizontalRule: false,
      paragraph: as ? false : undefined,
    }),
    createHighlightMark(highlightClassName),
    ...(as
      ? [
          Node.create({
            name: "paragraph",
            group: "block",
            content: "inline*",
            parseHTML() {
              return [{ tag: "p" }];
            },
            renderHTML({ HTMLAttributes }) {
              return [as, { ...HTMLAttributes, class: className }, 0];
            },
          }),
        ]
      : []),
  ];

  const editor = useEditor({
    extensions,
    content: jsonContent,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn("tiptap-highlight-content", !as && className),
      },
    },
  });

  // Update editor content when the content prop changes
  useEffect(() => {
    if (editor && jsonContent) {
      queueMicrotask(() => {
        if (editor && !editor.isDestroyed) {
          const currentContent = editor.getJSON();
          if (JSON.stringify(currentContent) !== JSON.stringify(jsonContent)) {
            editor.commands.setContent(jsonContent);
          }
        }
      });
    }
  }, [content, editor, jsonContent]);

  // Plain text fallback (legacy content)
  if (isPlainText) {
    const Element = as || "span";
    return (
      <Element
        className={cn("tiptap-highlight-content whitespace-pre-wrap", className)}
        onClick={handleTinaClick}
        {...props}
      >
        {content}
      </Element>
    );
  }

  if (!jsonContent || !editor) return null;

  return (
    <div
      onClick={handleTinaClick}
      onClickCapture={() => {
        setTimeout(() => {
          window.parent.postMessage({ type: "tiptap-highlight-click" }, "*");
        }, 500);
      }}
      {...props}
    >
      <EditorContent editor={editor} />
    </div>
  );
}

